import React, { useEffect, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { Grid, Paper, Typography, Button, Chip, Box, TextField, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import RefreshIcon from '@mui/icons-material/Refresh';
import FilterListIcon from '@mui/icons-material/FilterList';
import DataTable from 'react-data-table-component';
import { Bar, Pie } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement } from 'chart.js';
import { Client } from '../types';
import { getApiBaseUrl, getAppBaseUrl } from '../utils/baseUrl';
import { useLoading, acquireTokenSafe } from '../App';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement);

const customStyles = {
  headRow: {
    style: {
      backgroundColor: '#1976d2',
      color: '#fff',
      fontWeight: 500,
    },
  },
  headCells: {
    style: {
      padding: '12px',
      fontSize: '0.95rem',
      fontWeight: 600,
    },
  },
  rows: {
    style: {
      '&:nth-of-type(odd)': {
        backgroundColor: '#f8fafc',
      },
      '&:hover': {
        backgroundColor: '#e0f7fa',
      },
    },
  },
  cells: {
    style: {
      padding: '12px',
      fontSize: '0.9rem',
    },
  },
};

const getStateColor = (state: string) => {
  switch (state?.toLowerCase()) {
    case 'client':
      return '#388e3c';
    case 'suspended':
      return '#ff9800';
    case 'former client':
      return '#d32f2f';
    default:
      return '#757575';
  }
};

const PartnerReports = () => {
  const { instance } = useMsal();
  const { setLoading } = useLoading();
  const [clients, setClients] = useState<Client[]>([]);
  const [filteredClients, setFilteredClients] = useState<Client[]>([]);
  const [search, setSearch] = useState('');
  const [stateFilter, setStateFilter] = useState<string[]>([]);
  const [typeFilter, setTypeFilter] = useState<string[]>([]);
    const [serviceFilter, setServiceFilter] = useState<string[]>([]);
    const [partnerStatusFilter, setPartnerStatusFilter] = useState<string[]>([]);
    const [accountExecFilter, setAccountExecFilter] = useState<string[]>([]);
    const [durationFilter, setDurationFilter] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const fetchData = async () => {
    setLoading(true);
    try {
      const accounts = instance.getAllAccounts();
      const token = await acquireTokenSafe(instance, accounts);
      if (!token) { setLoading(false); return; }

      const authHeader = `Bearer ${token}`;
      const apiUrl = getApiBaseUrl();

      const response = await fetch(`${apiUrl}/api/clients?partnerOnly=true`, {
        method: 'GET',
        headers: {
          'Authorization': authHeader,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const data = await response.json();
      setClients(data.clients);
      setFilteredClients(data.clients);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [instance]);

    const handleFilter = () => {
        let filtered = clients;

        if (search) {
            filtered = filtered.filter(client => (
                client.ClientID.toLowerCase().includes(search.toLowerCase()) ||
                client.Title?.toLowerCase().includes(search.toLowerCase()) ||
                client.ClientState?.toLowerCase().includes(search.toLowerCase()) ||
                client.ClientType?.toLowerCase().includes(search.toLowerCase()) ||
                (typeof client.Services === 'string' && client.Services.toLowerCase().includes(search.toLowerCase())) ||
                client.CreatedDate?.toLowerCase().includes(search.toLowerCase()) ||
                client.OffBoardDate?.toLowerCase().includes(search.toLowerCase()) ||
                client.AccountExecutive?.toLowerCase().includes(search.toLowerCase()) ||
                client.Manager?.toLowerCase().includes(search.toLowerCase())
            ));
        }

        if (stateFilter.length > 0) {
            filtered = filtered.filter(client => stateFilter.includes(client.ClientState));
        }

        if (typeFilter.length > 0) {
            filtered = filtered.filter(client => typeFilter.includes(client.ClientType));
        }
        if (serviceFilter.length > 0) {
            filtered = filtered.filter(client =>
                typeof client.Services === 'string' &&
                client.Services.split(';').some(s => s.trim() && serviceFilter.includes(s.trim()))
            );
        }
        if (partnerStatusFilter.length > 0) {
            filtered = filtered.filter(client => partnerStatusFilter.includes(client.PartnerStatus));
        }
        if (accountExecFilter.length > 0) {
            filtered = filtered.filter(client => {
                const execOrManager = client.PartnerStatus === 'Gold Partner' ? client.AccountExecutive : client.Manager;
                return accountExecFilter.includes(execOrManager || '');
            });
        }
        if (durationFilter) {
            filtered = filtered.filter(client =>
                getDurationRange(client.CreatedDate, client.OffBoardDate) === durationFilter
            );
        }

        setFilteredClients(filtered);
    };

    useEffect(() => {
        handleFilter();
    }, [search, stateFilter, typeFilter, serviceFilter, partnerStatusFilter, accountExecFilter, durationFilter, clients]);

    const handleReset = () => {
        setSearch('');
        setStateFilter([]);
        setTypeFilter([]);
        setServiceFilter([]);
        setPartnerStatusFilter([]);
        setAccountExecFilter([]);
        setDurationFilter('');
        setFilteredClients(clients);
    };

  const uniqueStates = Array.from(new Set(clients.map(client => client.ClientState))).sort();
  const uniqueTypes = Array.from(new Set(clients.map(client => client.ClientType))).sort();
    const uniqueServices = Array.from(
        new Set(
            clients
                .filter(client => typeof client.Services === 'string')
                .flatMap(client => client.Services.split(';').map(s => s.trim()))
                .filter(s => s)
        )
    ).sort();
    const uniquePartnerStatuses = Array.from(new Set(clients.map(client => client.PartnerStatus))).sort();
    const uniqueAccountExecs = Array.from(
        new Set(clients.map(client => client.PartnerStatus === 'Gold Partner' ? client.AccountExecutive : client.Manager))
    ).filter(Boolean).sort();

    // KPI Calculations
    const goldPartnerCount = clients.filter(client => client.PartnerStatus === 'Gold Partner').length;
    const partnerCount = clients.filter(client => client.PartnerStatus === 'Partner').length;

    // KPI Filter Handlers
    const handleGoldPartnerFilter = () => {
        setPartnerStatusFilter(['Gold Partner']);
        setAccountExecFilter([]);
        setDurationFilter('');
        handleFilter();
    };

    const handlePartnerFilter = () => {
        setPartnerStatusFilter(['Partner']);
        setAccountExecFilter([]);
        setDurationFilter('');
        handleFilter();
    };

    // Client Duration Calculation
    const getClientSince = (client: Client) => {
        const created = new Date(client.CreatedDate);
        const endDate = client.OffBoardDate ? new Date(client.OffBoardDate) : new Date();
        const diffMs = endDate.getTime() - created.getTime();
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        const years = Math.floor(diffDays / 365);
        const months = Math.floor((diffDays % 365) / 30);
        const days = diffDays % 30;
        let duration = '';
        if (years > 0) duration += `${years}y `;
        if (months > 0 || years > 0) duration += `${months}m `;
        duration += `${days}d`;
        return duration.trim();
    };

    // Duration Chart Data
    const durationRanges = ['< 3 months', '3-6 months', '6-12 months', '1-2 years', '2-5 years', '5+ years'];
    const getDurationRange = (createdDate: string, offBoardDate?: string) => {
        const created = new Date(createdDate);
        const endDate = offBoardDate ? new Date(offBoardDate) : new Date();
        const diffMs = endDate.getTime() - created.getTime();
        const diffMonths = diffMs / (1000 * 60 * 60 * 24 * 30);
        if (diffMonths < 3) return '< 3 months';
        if (diffMonths < 6) return '3-6 months';
        if (diffMonths < 12) return '6-12 months';
        if (diffMonths < 24) return '1-2 years';
        if (diffMonths < 60) return '2-5 years';
        return '5+ years';
    };

    const goldPartnerDurations = durationRanges.map(range =>
        clients.filter(c => c.PartnerStatus === 'Gold Partner' && getDurationRange(c.CreatedDate, c.OffBoardDate) === range).length
    );
    const partnerDurations = durationRanges.map(range =>
        clients.filter(c => c.PartnerStatus === 'Partner' && getDurationRange(c.CreatedDate, c.OffBoardDate) === range).length
    );

    const durationChartData = {
        labels: durationRanges,
        datasets: [
            {
                label: 'Gold Partners',
                data: goldPartnerDurations,
                backgroundColor: '#FFD700',
            },
            {
                label: 'Partners',
                data: partnerDurations,
                backgroundColor: '#C0C0C0',
            },
        ],
    };

    const handleDurationChartClick = (event: any, elements: any) => {
        if (elements.length > 0) {
            const element = elements[0];
            const datasetIndex = element.datasetIndex;
            const index = element.index;
            const status = datasetIndex === 0 ? 'Gold Partner' : 'Partner';
            const range = durationRanges[index];
            setPartnerStatusFilter([status]);
            setDurationFilter(range);
            setAccountExecFilter([]);
            handleFilter();
        }
    };

    const durationChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            title: { display: true, text: 'Partner Duration Distribution' },
            legend: { position: 'top' as const },
        },
        scales: {
            x: { stacked: true },
            y: { stacked: true, title: { display: true, text: 'Number of Partners' } },
        },
        onClick: handleDurationChartClick,
    };

    // Pie Chart Data for Account Executives (Gold) and Managers (Partners)
    const accountExecData = uniqueAccountExecs.map(exec =>
        clients.filter(c =>
            (c.PartnerStatus === 'Gold Partner' && c.AccountExecutive === exec) ||
            (c.PartnerStatus === 'Partner' && c.Manager === exec)
        ).length
    );

    const accountExecChartData = {
        labels: uniqueAccountExecs,
        datasets: [{
            data: accountExecData,
            backgroundColor: [
                '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF', '#FF9F40',
                '#C9CB3F', '#E7E9ED', '#7D4F6D', '#D4A5A5',
            ],
        }],
    };

    const handleAccountExecChartClick = (event: any, elements: any) => {
        if (elements.length > 0) {
            const element = elements[0];
            const index = element.index;
            const selectedExec = uniqueAccountExecs[index];
            setAccountExecFilter([selectedExec]);
            setPartnerStatusFilter(['Gold Partner', 'Partner']);
            setDurationFilter('');
            handleFilter();
        }
    };

    const accountExecChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            title: { display: true, text: 'Account Executives (Gold) & Managers (Partners) Distribution' },
            legend: { position: 'top' as const },
        },
        onClick: handleAccountExecChartClick,
    };

    const columns = [
        {
            name: 'Client ID',
            selector: (row: Client) => row.ClientID,
            sortable: true,
            cell: (row: Client) => (
                <Box display="flex" alignItems="center">
                    <a
                        href={`https://kinore.senta.co/c/v/${row.ClientID}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: '#1976d2', textDecoration: 'none' }}
                        onMouseOver={e => e.currentTarget.style.textDecoration = 'underline'}
                        onMouseOut={e => e.currentTarget.style.textDecoration = 'none'}
                    >
                        {row.ClientID}
                    </a>
                    <OpenInNewIcon
                        sx={{ fontSize: '14px', marginLeft: '4px', color: '#1976d2' }}
                        titleAccess="Opens in a new tab"
                    />
                </Box>
            ),
        },
        { name: 'Title', selector: (row: Client) => row.Title, sortable: true },
        {
            name: 'State',
            selector: (row: Client) => row.ClientState,
            sortable: true,
            cell: (row: Client) => (
                <Chip
                    label={row.ClientState || 'Unknown'}
                    sx={{
                        backgroundColor: getStateColor(row.ClientState || ''),
                        color: '#fff',
                        fontWeight: 'bold',
                        borderRadius: '16px',
                        height: '24px',
                    }}
                />
            ),
        },
        { name: 'Type', selector: (row: Client) => row.ClientType, sortable: true },
        { name: 'Client Duration', selector: (row: Client) => getClientSince(row), sortable: true },
        { name: 'Partner Status', selector: (row: Client) => row.PartnerStatus, sortable: true },
        {
            name: 'Account Executive',
            selector: (row: Client) => row.PartnerStatus === 'Gold Partner' ? row.AccountExecutive : row.Manager,
            sortable: true,
            cell: (row: Client) => (
                row.PartnerStatus === 'Gold Partner' ? row.AccountExecutive : row.Manager || 'Unassigned'
            ),
        },
    ];

    const ExpandedComponent = ({ data }: { data: Client }) => (
        <Box sx={{ p: 2 }}>
            <Typography variant="subtitle1">Services:</Typography>
            {typeof data.Services === 'string' && data.Services ? (
                data.Services.split(';').map((service, index) => (
                    service.trim() && (
                        <Chip
                            key={index}
                            label={service.trim()}
                            sx={{
                                backgroundColor: '#e0e0e0',
                                color: '#424242',
                                fontWeight: 'medium',
                                borderRadius: '16px',
                                height: '24px',
                                m: 0.5,
                            }}
                        />
                    )
                ))
            ) : (
                <Typography>No Services</Typography>
            )}
            <Typography variant="subtitle1" sx={{ mt: 1 }}>Created Date:</Typography>
            <Typography>{data.CreatedDate}</Typography>
            <Typography variant="subtitle1" sx={{ mt: 1 }}>Off Board Date:</Typography>
            <Typography>{data.OffBoardDate || 'N/A'}</Typography>
            <Typography variant="subtitle1" sx={{ mt: 1 }}>Manager:</Typography>
            <Typography>{data.Manager || 'N/A'}</Typography>
        </Box>
    );

    const downloadCSV = () => {
        const headers = columns.map(col => col.name).join(',');
        const rows = filteredClients.map(client =>
            columns.map(col => {
                const value = col.selector(client);
                return `"${(value || '').toString().replace(/"/g, '""')}"`;
            }).join(',')
        ).join('\n');
        const csvContent = `${headers}\n${rows}`;

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = 'partner_reports.csv';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    if (error) return <div>Error: {error}</div>;

    return (
        <div style={{ padding: 20, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
            <Typography variant="h1" gutterBottom>Partner Reports</Typography>
            <Grid container spacing={3}>
                {/* KPI Row */}
                <Grid size={{ xs: 12 }}>
                    <Grid container spacing={3}>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <Paper
                                elevation={3}
                                sx={{ p: 2, textAlign: 'center', cursor: 'pointer' }}
                                onClick={handleGoldPartnerFilter}
                            >
                                <Typography variant="h6">
                                    Gold Partners <FilterListIcon sx={{ verticalAlign: 'middle', ml: 1 }} />
                                </Typography>
                                <Typography variant="h4">{goldPartnerCount}</Typography>
                            </Paper>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <Paper
                                elevation={3}
                                sx={{ p: 2, textAlign: 'center', cursor: 'pointer' }}
                                onClick={handlePartnerFilter}
                            >
                                <Typography variant="h6">
                                    Partners <FilterListIcon sx={{ verticalAlign: 'middle', ml: 1 }} />
                                </Typography>
                                <Typography variant="h4">{partnerCount}</Typography>
                            </Paper>
                        </Grid>
                    </Grid>
                </Grid>
                {/* Chart Row */}
                <Grid size={{ xs: 12 }}>
                    <Grid container spacing={3}>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Paper elevation={3} sx={{ p: 3 }}>
                                <Box sx={{ height: '400px' }}>
                                    <Bar data={durationChartData} options={durationChartOptions} />
                                </Box>
                            </Paper>
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Paper elevation={3} sx={{ p: 3 }}>
                                <Box sx={{ height: '400px' }}>
                                    <Pie data={accountExecChartData} options={accountExecChartOptions} />
                                </Box>
                            </Paper>
                        </Grid>
                    </Grid>
                </Grid>
                {/* DataTable */}
                <Grid size={{ xs: 12 }}>
                    <Paper elevation={3} sx={{ p: 3 }}>
                        <Box display="flex" flexWrap="wrap" gap={2} mb={2}>
                            <TextField
                                label="Search All Fields"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                sx={{ flex: '1 1 200px', minWidth: '200px' }}
                            />
                            <FormControl sx={{ flex: '1 1 200px', minWidth: '200px' }}>
                                <InputLabel>Client State</InputLabel>
                                <Select
                                    multiple
                                    value={stateFilter}
                                    onChange={(e) => setStateFilter(e.target.value as string[])}
                                    label="Client State"
                                    renderValue={(selected) => (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {(selected as string[]).map((value) => (
                                                <Chip key={value} label={value} />
                                            ))}
                                        </Box>
                                    )}
                                >
                                    {uniqueStates.map(state => (
                                        <MenuItem key={state} value={state}>{state}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl sx={{ flex: '1 1 200px', minWidth: '200px' }}>
                                <InputLabel>Client Type</InputLabel>
                                <Select
                                    multiple
                                    value={typeFilter}
                                    onChange={(e) => setTypeFilter(e.target.value as string[])}
                                    label="Client Type"
                                    renderValue={(selected) => (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {(selected as string[]).map((value) => (
                                                <Chip key={value} label={value} />
                                            ))}
                                        </Box>
                                    )}
                                >
                                    {uniqueTypes.map(type => (
                                        <MenuItem key={type} value={type}>{type}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl sx={{ flex: '1 1 200px', minWidth: '200px' }}>
                                <InputLabel>Service</InputLabel>
                                <Select
                                    multiple
                                    value={serviceFilter}
                                    onChange={(e) => setServiceFilter(e.target.value as string[])}
                                    label="Service"
                                    renderValue={(selected) => (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {(selected as string[]).map((value) => (
                                                <Chip key={value} label={value} />
                                            ))}
                                        </Box>
                                    )}
                                >
                                    {uniqueServices.map(service => (
                                        <MenuItem key={service} value={service}>{service}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl sx={{ flex: '1 1 200px', minWidth: '200px' }}>
                                <InputLabel>Partner Status</InputLabel>
                                <Select
                                    multiple
                                    value={partnerStatusFilter}
                                    onChange={(e) => setPartnerStatusFilter(e.target.value as string[])}
                                    label="Partner Status"
                                    renderValue={(selected) => (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {(selected as string[]).map((value) => (
                                                <Chip key={value} label={value} />
                                            ))}
                                        </Box>
                                    )}
                                >
                                    {uniquePartnerStatuses.map(status => (
                                        <MenuItem key={status} value={status}>{status}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <Button
                                variant="contained"
                                onClick={handleReset}
                                startIcon={<RefreshIcon />}
                            >
                                Reset Filters
                            </Button>
                        </Box>
                        <DataTable
                            columns={columns}
                            data={filteredClients}
                            pagination
                            highlightOnHover
                            striped
                            customStyles={customStyles}
                            responsive
                            expandableRows
                            expandableRowsComponent={ExpandedComponent}
                            paginationPerPage={rowsPerPage}
                            paginationRowsPerPageOptions={[5, 10, 25, 50, 100]}
                            onChangeRowsPerPage={(newRowsPerPage) => setRowsPerPage(newRowsPerPage)}
                        />
                        <Box sx={{ mt: 2, textAlign: 'right' }}>
                            <Button
                                variant="contained"
                                onClick={downloadCSV}
                                startIcon={<FileDownloadIcon />}
                                sx={{
                                    background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
                                    borderRadius: '25px',
                                    padding: '8px 20px',
                                    textTransform: 'none',
                                    fontWeight: 'bold',
                                    boxShadow: '0 3px 5px 2px rgba(25, 118, 210, .3)',
                                    '&:hover': {
                                        background: 'linear-gradient(45deg, #1565c0 30%, #2196f3 90%)',
                                        boxShadow: '0 5px 15px rgba(25, 118, 210, .4)',
                                    },
                                }}
                            >
                                Export to CSV
                            </Button>
                        </Box>
                    </Paper>
                </Grid>
            </Grid>
        </div>
    );
};

export default PartnerReports;